/**
 * Icon mapping: legacy lucide (Taiga @taiga-ui/icons) + custom fvIcon names -> Phosphor Icons v2 (Figma Token Design Library).
 * The TUI_ICON_RESOLVER (app.config.ts) consults these maps and returns
 * 'assets/phosphor/<name>.svg' when a mapping exists, otherwise falls back to the legacy asset path.
 *
 * DO NOT hand-edit blindly.
 */

/** lucide-kebab (from '@tui.<name>' and normalized 'tuiIcon<Name>') -> phosphor v2 regular filename. */
export const LUCIDE_TO_PHOSPHOR: Readonly<Record<string, string>> = {
  activity: 'pulse',
  'alert-circle': 'warning-circle',
  'alert-triangle': 'warning',
  'align-center': 'text-align-center',
  'align-left': 'text-align-left',
  'align-right': 'text-align-right',
  archive: 'archive',
  'arrow-down': 'arrow-down',
  'arrow-left': 'arrow-left',
  'arrow-right': 'arrow-right',
  'arrow-right-left': 'arrows-left-right',
  'arrow-up': 'arrow-up',
  bell: 'bell',
  bold: 'text-b',
  bookmark: 'bookmark',
  // Date inputs (tuiInputDate/Multi/Range default iconEnd '@tui.calendar') must use the
  // plain calendar per Figma (Icon/20px/CalendarBlank); phosphor 'calendar' regular ships a
  // '12' glyph. 'calendar' is only rendered by date inputs (+ the e-sign date-field marker),
  // so remapping it here scopes the fix to exactly those.
  calendar: 'calendar-blank',
  // 'calendar-clock' has no consumers yet — reserved as a source key for the planned
  // Maturity-Date field; mapped to the same asset for parity.
  'calendar-clock': 'calendar-blank',
  check: 'check',
  'check-circle': 'check-circle',
  'chevron-down': 'caret-down',
  'chevron-left': 'caret-left',
  'chevron-right': 'caret-right',
  'chevron-up': 'caret-up',
  'circle-alert': 'warning-circle',
  'circle-check': 'check-circle',
  'circle-dollar-sign': 'currency-circle-dollar',
  'circle-help': 'question',
  'circle-user-round': 'user-circle',
  'circle-x': 'x-circle',
  clipboard: 'clipboard',
  clock: 'clock',
  'clock-clockwise': 'clock-clockwise',
  close: 'x',
  'cloud-upload': 'cloud-arrow-up',
  code: 'code',
  copy: 'copy',
  'dollar-sign': 'currency-dollar',
  download: 'download',
  'download-simple': 'download-simple',
  drag: 'dots-six-vertical',
  edit: 'pencil-simple-line',
  'edit-3': 'pencil-simple-line',
  'ellipsis-vertical': 'dots-three-vertical',
  // FDS-4438: Figma uses a bespoke "Share" glyph (not shipped by Phosphor) — served from assets/svg-icons.
  'external-link': 'fvIconShare',
  eye: 'eye',
  'eye-off': 'eye-slash',
  file: 'file',
  'file-text': 'file-text',
  'file-text-large': 'file-text',
  folder: 'folder',
  gauge: 'speedometer',
  'git-branch': 'git-branch',
  globe: 'globe',
  grip: 'dots-six-vertical',
  'grip-vertical': 'dots-six-vertical',
  handshake: 'handshake',
  hash: 'hash',
  heart: 'heart',
  'help-circle': 'question',
  home: 'house',
  inbox: 'tray',
  info: 'info',
  italic: 'text-italic',
  key: 'key',
  landmark: 'bank',
  layout: 'layout',
  link: 'link',
  list: 'list',
  lock: 'lock',
  'log-out': 'sign-out',
  mail: 'envelope',
  maximize: 'corners-out',
  monitor: 'monitor',
  'more-vertical': 'dots-three-vertical',
  package: 'package',
  pencil: 'pencil-simple-line',
  'pencil-line': 'pencil-simple-line',
  phone: 'phone',
  plus: 'plus',
  'plus-circle': 'plus-circle',
  'plus-large': 'plus',
  redo: 'arrow-clockwise',
  'refresh-ccw': 'arrow-counter-clockwise',
  'refresh-cw': 'arrow-clockwise',
  repeat: 'repeat',
  'rotate-ccw': 'arrow-counter-clockwise',
  'rotate-cw': 'arrow-clockwise',
  search: 'magnifying-glass',
  settings: 'gear',
  shield: 'shield',
  star: 'star',
  store: 'storefront',
  table: 'table',
  'toggle-right': 'toggle-right',
  trash: 'trash',
  'trash-2': 'trash',
  'trash-2-large': 'trash',
  'triangle-alert': 'warning',
  type: 'text-t',
  underline: 'text-underline',
  undo: 'arrow-counter-clockwise',
  'undo-2': 'arrow-counter-clockwise',
  unlock: 'lock-open',
  upload: 'upload-simple',
  'upload-cloud': 'cloud-arrow-up',
  user: 'user',
  'user-check': 'user-check',
  'user-large': 'user',
  'user-plus': 'user-plus',
  'user-round': 'user',
  'user-x': 'user-minus',
  users: 'users',
  voicemail: 'voicemail',
  x: 'x',
  'x-circle': 'x-circle',
  zap: 'lightning',
};

/** Custom 'fvIcon<Name>' whose glyph is a generic concept covered by Phosphor -> phosphor v2 regular filename. */
export const FVICON_TO_PHOSPHOR: Readonly<Record<string, string>> = {
  fvIconAccountActivationDot: 'dot',
  // Selected Deal Information card: Figma "Anchor" field uses Icon/20px/UserCircle,
  // not the legacy nautical-anchor glyph. Name is used only by that card + its manual-entry twin.
  fvIconAnchor: 'user-circle',
  fvIconApprove: 'check',
  fvIconArrowLeft: 'arrow-left',
  fvIconArrowUDownLeft: 'arrow-u-down-left',
  fvIconCarbonUserSettings: 'user-gear',
  fvIconCheckCircle: 'check-circle',
  fvIconCheckWhite: 'check',
  fvIconCompanyDetails: 'identification-card',
  // fvIconCongratulationsCheck is intentionally NOT mapped to phosphor: the account-activation
  // success screen needs the filled Material-style check-square from the design, which phosphor
  // 'check-square' (outline) does not match. Omitting it lets the resolver serve the custom
  // assets/svg-icons/fvIconCongratulationsCheck.svg, coloured via --fv-color-content-positive.
  // Selected Deal Information card: Figma "Currency" field uses Icon/20px/CurrencyCircleDollar.
  fvIconCurrencyExchange: 'currency-circle-dollar',
  fvIconDashboardCheckCircle: 'check-circle',
  fvIconDashboardViewLink: 'paper-plane-tilt',
  fvIconDataGridHeaderQuestionCircle: 'question',
  fvIconDataGridRowLock: 'lock-simple',
  fvIconDeal: 'handshake',
  fvIconFunder: 'bank',
  fvIconInputFieldCleaner: 'x',
  fvIconInstrumentApprovalReject: 'x-circle',
  fvIconInstrumentApprovalReview: 'check-circle',
  fvIconInstrumentApprovalUndoRejection: 'arrow-counter-clockwise',
  // Selected Deal Information card: Figma "Limit" field uses Icon/20px/Database,
  // not warning-circle. 'fvIconLimit' is consumed only by that card + its manual-entry twin.
  fvIconLimit: 'database',
  fvIconLock: 'lock',
  fvIconLockKeyOpen: 'lock-key-open',
  fvIconMingcuteTimeFill: 'clock',
  fvIconMobileDevice: 'device-mobile',
  fvIconNavMenuHelp: 'question',
  fvIconNavMenuInstrumentApproval: 'check-circle',
  fvIconNavMenuNplStatusApprovals: 'list-checks',
  // These sidebar items carry bespoke names (not generic '@tui.*') so a white-label build can
  // override the sidebar icon in isolation; the fallbacks keep every other build on the same glyph.
  fvIconNavMenuRejected: 'x-circle',
  fvIconNavMenuCustomFields: 'list',
  fvIconNavMenuDigitalContracts: 'file-text',
  fvIconNavMenuNotificationTemplates: 'bell',
  fvIconNavMenuWebhookManagement: 'lightning',
  fvIconNavMenuSystemHealthNotifications: 'heart',
  fvIconNavMenuCompanySecuritySettings: 'shield',
  fvIconNavMenuAuditTrail: 'voicemail',
  fvIconPrimaryArrowRightLarge: 'arrow-right',
  fvIconQuestionCircle: 'question',
  fvIconReplace: 'arrow-clockwise',
  fvIconRightArrow: 'caret-right',
  fvIconSetupPasswordWarningOctagon: 'warning-octagon',
  fvIconSolarCheckRead: 'checks',
  fvIconSolarUnread: 'check',
  fvIconSpinner: 'spinner',
  fvIconTableExpandRowRight: 'caret-circle-right',
};
